// KSD Pursuit Cruiser: a Japanese-style patrol sedan. Fixed livery (white upper, slate-blue lower), roof light bar, push bar.
// Numbers in hd px (see docs/CAR_ART.md). The siren glow is a separate runtime sprite, so the bar bulbs are drawn unlit.
import { K, dk, lt, mix } from '../hd-kit';
import { registerCarModel, type CarModel, type V3 } from '../car-drawn';

const BODY: [number, number][] = [[-25, 3], [25, 3], [25, 8.2], [21.5, 8.9], [8, 9.8], [-16, 9.8], [-22.5, 9.5], [-25, 8.4]];
const CABIN: [number, number][] = [[-17, 9.8], [7, 9.8], [2.5, 19.2], [-14, 19.2]];
const HW = 11.4, HWC = 9.6, ROOF = 19.2;
const BLUE = lt(mix(K.slate, K.cyan, .3), .12), BAND = 8.5; // door line: slate-blue below, white above
const white = (n: V3, up: boolean) => up ? K.white : n[1] > 0 ? dk(K.white, .3) : lt(K.white, 0);

export const police: CarModel = {
  body: BODY, hw: HW, cabin: CABIN, hwc: HWC, axles: [16, -16], wheelR: 4.2,
  bodyColor: () => BLUE,
  bodyEdge(i, P) {
    const { fill, endRect } = P;
    if (i === 1) return () => { // front: bumper, dark grille, warm headlamps, push bar
      fill(endRect(25, -HW, HW, 3, 5.2), K.metal); fill(endRect(25, -HW, HW, 3, 3.8), K.metalLo);
      fill(endRect(25, -4.8, 4.8, 5.4, 7.9), K.ink); for (let v = -4; v < 4; v += 2) fill(endRect(25, v, v + 1, 5.8, 7.5), K.metalLo);
      for (const s of [1, -1]) { fill(endRect(25, s * 6.6, s * 10.8, 5.6, 8), K.ink); fill(endRect(25, s * 7, s * 10.4, 5.9, 7.7), P.lampOn ? K.warm : K.metalLo); if (P.lampOn) fill(endRect(25, s * 7.4, s * 10, 6.7, 7.7), K.warmHi); }
      for (const s of [1, -1]) fill([[25, s * 5, 4.6], [27.6, s * 5, 4.6], [27.6, s * 5 + 1, 4.6], [25, s * 5 + 1, 4.6]].map(p => [p[0], p[1], p[2] + 0] as V3), K.metalLo);
      fill(endRect(27.6, -8, 8, 3.8, 7.2), K.ink); fill(endRect(27.6, -7.4, 7.4, 4.4, 6.6), K.metalLo); fill(endRect(27.6, -7.4, 7.4, 6, 6.6), K.metalHi);
      fill(endRect(27.6, -.5, .5, 4.4, 6.6), K.ink);
      fill(endRect(27.6, -8, 8, 7.2, 7.7), K.metalHi); fill([[25, -8, 7.2], [27.6, -8, 7.2], [27.6, 8, 7.2], [25, 8, 7.2]], lt(K.metalHi, .2)); // push bar: lit top edge
    };
    if (i === 3) return () => { fill([[21, -2.2, 9], [21, 2.2, 9], [9, 2.2, 9.8], [9, -2.2, 9.8]], lt(P.body, .3)); }; // hood bulge
    if (i === 7) return () => { // rear: bumper, red tail lamps, plate
      fill(endRect(-25, -HW, HW, 3, 5.2), K.metal); fill(endRect(-25, -HW, HW, 3, 3.8), K.metalLo);
      fill(endRect(-25, -HW, HW, 5.4, 8.4), P.body);
      for (const s of [1, -1]) { fill(endRect(-25, s * 6.2, s * 10.8, 5.6, 8), K.ink); fill(endRect(-25, s * 6.6, s * 10.4, 5.9, 7.7), P.lampOn ? K.red : K.metalLo); if (P.lampOn) fill(endRect(-25, s * 7, s * 9, 6.5, 7.3), K.pink); }
      fill(endRect(-25, -3, 3, 5, 6.8), K.white); fill(endRect(-25, -2.2, 2.2, 5.6, 6.2), K.metalLo);
    };
    return undefined;
  },
  bodySide(_s, v, P) {
    const { fill, sideRect } = P, seam = dk(P.body, .8);
    const north = v < 0;
    return () => {
      if (!P.wrecked) fill(sideRect(v, -22, 22, BAND, 9.8), north ? K.white : dk(K.white, .3)); // white upper band
      if (!P.wrecked) fill(sideRect(v, -22, 22, BAND - .5, BAND), north ? K.paint : dk(K.paint, .3));
      fill(sideRect(v, -4.5, -3.9, 3.2, 9.6), seam); fill(sideRect(v, 8, 8.6, 3.2, 9.3), seam); fill(sideRect(v, -18, -17.4, 5, 9.3), seam);
      fill(sideRect(v, -2.4, -.4, 6.6, 7), K.metalHi);
      if (P.wrecked) { // the white band is gone; a dented rear door and a scrape along the front wing
        fill([[-15, v, 5], [-9, v, 4.4], [-7, v, 7.6], [-13, v, 8.4]], dk(P.body, .5)); fill([[-15, v, 5], [-12.5, v, 4.8], [-13, v, 5.8]], lt(P.body, .5));
        fill(sideRect(v, 10, 20, 6, 6.6), lt(P.body, .35)); fill(sideRect(v, 12, 18, 3.2, 4.6), Math.sign(v) > 0 ? K.woodLo : dk(P.body, .6));
      }
      if (!P.wrecked) { // door emblem: just the gold roundel with a white core (the sides carry no other colour)
        const s = Math.sign(v);
        // On the front door (seams at -3.9 and 8, handle at -2.4..-.4), so it stays a door badge from either side.
        fill(P.disc(3, v + s * .01, 6, 2.6), K.ink); fill(P.disc(3, v + s * .02, 6, 2), K.amber); fill(P.disc(3, v + s * .03, 6, .9), K.white);
      }
    };
  },
  cabinEdge(i, P, { n, at }) {
    const { fill } = P;
    const wc = P.wrecked ? P.tone(n) : white(n, false);
    if (i === 1) return { color: P.wrecked ? P.tone(n) : n[1] < 0 ? K.white : K.white, decal: () => { // windscreen
      if (P.toward(n) < .6) return;
      const pane = [at(.12, -HWC + .9), at(.88, -HWC + .9), at(.88, HWC - .9), at(.12, HWC - .9)]; fill(pane, P.glassTone(n));
      if (!P.wrecked) fill([at(.12, -HWC + .9), at(.88, -HWC + .9), at(.88, -HWC + 1.9), at(.12, -HWC + 1.9)], K.glassHi); P.cracks(pane);
    } };
    if (i === 3) return { color: wc, decal: () => { // rear window
      if (P.toward(n) < .6) return; const pane = [at(.14, -HWC + 1.2), at(.86, -HWC + 1.2), at(.86, HWC - 1.2), at(.14, HWC - 1.2)]; fill(pane, P.glassTone(n)); P.cracks(pane);
    } };
    return { color: P.wrecked ? lt(P.body, .3) : K.white, decal: () => { // roof with the light bar
      const z = ROOF + .01, u0 = -9, u1 = -2, b = 6.6;
      const top = z + 1;
      const box = (a: number, c: number, v0: number, v1: number, z0: number, z1: number, col: string, hi: string) => {
        const f: [V3, V3[], string][] = [
          [[1, 0, 0], [[c, v0, z0], [c, v1, z0], [c, v1, z1], [c, v0, z1]], col], [[-1, 0, 0], [[a, v0, z0], [a, v1, z0], [a, v1, z1], [a, v0, z1]], col],
          [[0, 1, 0], [[a, v1, z0], [c, v1, z0], [c, v1, z1], [a, v1, z1]], dk(col, .3)], [[0, -1, 0], [[a, v0, z0], [c, v0, z0], [c, v0, z1], [a, v0, z1]], col],
        ];
        const order = f.filter(x => P.toward(x[0]) > 0).sort((p, q) => P.toward(p[0]) - P.toward(q[0]));
        for (const [, pts, c0] of order) fill(pts, c0);
        fill([[a, v0, z1], [c, v0, z1], [c, v1, z1], [a, v1, z1]], hi);
      };
      const wr = P.wrecked;
      if (wr) { // the light bar is torn loose: one end still on the roof, the other hanging over the south side, lenses smashed
        const p = (u: number, v: number, dz: number): V3 => { const t = (v + 4) / (HWC + 7); return [u, v, z + 1.6 - t * 5.4 + dz]; };
        fill([p(u0, -4, -1.6), p(u1, -4, -1.6), p(u1, HWC + 3, -1.6), p(u0, HWC + 3, -1.6)].map(q => [q[0], q[1] + .01, q[2]] as V3), K.ink); // underside shadow
        fill([p(u0, -4, 0), p(u1, -4, 0), p(u1, HWC + 3, 0), p(u0, HWC + 3, 0)], K.metalLo); // top
        fill([p(u1, -4, -1.6), p(u1, HWC + 3, -1.6), p(u1, HWC + 3, 0), p(u1, -4, 0)], K.ink); // front face
        fill([p(u0 + 1, -2.5, .01), p(u1 - 1, -2.5, .01), p(u1 - 1, 1.5, .01), p(u0 + 1, 1.5, .01)], dk(K.cyan, .6)); fill([p(u0 + 1.5, 4, .01), p(u1 - 2, 4, .01), p(u1 - 2, 7, .01), p(u0 + 1.5, 7, .01)], K.redDk); // dead lenses
        fill([[u0 - 1, -6, z], [u0 + 2, -7, z], [u0 + 1, -4.5, z]], K.woodLo); // scorch where the bar tore off
        return;
      }
      box(u0, u1, -b, b, z, top, K.ink, K.metalLo); // housing
      const bulbs = (v0: number, v1: number, col: string) => {
        box(u0 + .8, u1 - .8, v0, v1, top, top + 1.8, wr ? K.metalLo : col, wr ? K.metalLo : lt(col, .35));
      };
      bulbs(-b + .6, -.6, K.cyan); bulbs(.6, b - .6, K.red);
    } };
  },
  cabinSide(s, v, P) {
    const n: V3 = [0, s, 0], { fill } = P;
    return () => {
      if (!P.wrecked) fill([[-17, v, 9.8], [7, v, 9.8], [2.5, v, ROOF], [-14, v, ROOF]], s < 0 ? K.white : dk(K.white, .3)); // white cabin wall
      const gt = P.glassTone(n);
      const rear: V3[] = [[-15.4, v, 11.6], [-13.2, v, 18.2], [-6, v, 18.2], [-6, v, 11.6]];
      const front: V3[] = [[-4.6, v, 11.6], [-4.6, v, 18.2], [.6, v, 18.2], [4.4, v, 11.6]];
      fill(rear, gt); fill(front, gt); P.cracks(rear); P.cracks(front);
      if (!P.wrecked) fill([[-15.4, v, 11.6], [-13.2, v, 18.2], [-12.4, v, 18.2], [-14.6, v, 11.6]], K.glassHi);
    };
  },
};
registerCarModel('police', police);
